/**
 * LED Power Budget Calculator
 *
 * Estimates current draw for the keyboard system and calculates
 * the maximum safe pressed brightness to stay within converter limits.
 *
 * All current values in mA. SK6812 RGBW LEDs draw up to 20mA per channel.
 */

// SK6812 spec: 20mA per color channel at full brightness
const MA_PER_LED_CHANNEL = 20;
// MCU + Hall sensors + CAN transceiver per keyboard module
const ELECTRONICS_PER_MODULE_MA = 80;
// STM32H743 main module + peripherals
const MAIN_MODULE_MA = 300;

export interface PowerBudgetParams {
  columns: number;
  rows: number;
  keysPerModule: number;
  globalBrightness: number;
  idleColor: [number, number, number];
  idleBrightness: number;
  pressedColor: [number, number, number];
  pressedBrightness: number;
  maxSimultaneousPressed: number;
  converterAmps: number;
}

/**
 * Estimate current draw for a single LED given its color and effective brightness.
 * Each RGB channel draws up to 20mA proportional to its value/255 * brightness.
 * Note: SK6812 W (white) channel is intentionally excluded — firmware sets W=0
 * in all LED commands, so including it would overestimate power draw.
 */
function ledCurrentMa(color: [number, number, number], effectiveBrightness: number): number {
  const channelSum = (color[0] + color[1] + color[2]) / 255;
  return channelSum * effectiveBrightness * MA_PER_LED_CHANNEL;
}

/**
 * Estimate total system current draw in mA.
 */
export function estimateCurrentDraw(params: PowerBudgetParams): number {
  const {
    columns, rows, keysPerModule,
    globalBrightness,
    idleColor, idleBrightness,
    pressedColor, pressedBrightness,
    maxSimultaneousPressed,
  } = params;

  const numModules = columns * rows;
  const totalKeys = numModules * keysPerModule;
  const pressedKeys = Math.min(maxSimultaneousPressed, totalKeys);
  const idleKeys = totalKeys - pressedKeys;

  const idleEffective = idleBrightness * globalBrightness;
  const pressedEffective = pressedBrightness * globalBrightness;

  const perIdleLed = ledCurrentMa(idleColor, idleEffective);
  const perPressedLed = ledCurrentMa(pressedColor, pressedEffective);

  return MAIN_MODULE_MA
    + numModules * ELECTRONICS_PER_MODULE_MA
    + idleKeys * perIdleLed
    + pressedKeys * perPressedLed;
}

/**
 * Calculate the maximum pressed brightness (0-1.0) that keeps
 * total current within the converter budget.
 *
 * Solves: totalCurrent(pressedBrightness=x) <= converterAmps * 1000
 *
 * Returns 1.0 if no clamping needed, or the max safe value otherwise.
 */
export function calculateMaxPressedBrightness(params: PowerBudgetParams): number {
  const {
    columns, rows, keysPerModule,
    globalBrightness,
    idleColor, idleBrightness,
    pressedColor,
    maxSimultaneousPressed,
    converterAmps,
  } = params;

  const budgetMa = converterAmps * 1000;
  const numModules = columns * rows;
  const totalKeys = numModules * keysPerModule;
  const pressedKeys = Math.min(maxSimultaneousPressed, totalKeys);
  const idleKeys = totalKeys - pressedKeys;

  const idleEffective = idleBrightness * globalBrightness;
  const perIdleLed = ledCurrentMa(idleColor, idleEffective);

  // Fixed overhead: main module + module electronics + idle LEDs
  const fixedMa = MAIN_MODULE_MA
    + numModules * ELECTRONICS_PER_MODULE_MA
    + idleKeys * perIdleLed;

  const remainingMa = budgetMa - fixedMa;
  if (remainingMa <= 0) return 0;

  // Per-pressed-LED current at full pressedBrightness=1.0:
  // ledCurrentMa(pressedColor, 1.0 * globalBrightness)
  const perPressedAtFull = ledCurrentMa(pressedColor, globalBrightness);
  const totalPressedAtFull = pressedKeys * perPressedAtFull;

  if (totalPressedAtFull <= 0) return 1.0;

  const maxBrightness = remainingMa / totalPressedAtFull;
  return Math.min(1.0, maxBrightness);
}
